import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Badge,
  SimpleGrid,
  Icon,
  HStack,
  Divider,
} from '@chakra-ui/react';
import InvestmentCalculator from '../components/calculators/InvestmentCalculator';
import { FaCalculator, FaChartLine, FaPiggyBank } from 'react-icons/fa';

const FeatureCard = ({ icon, title, description }) => (
  <Box
    bg={useColorModeValue('white', 'gray.800')}
    p={6}
    borderRadius="lg"
    shadow="md"
    transition="all 0.3s"
    _hover={{ transform: 'translateY(-5px)', shadow: 'lg' }}
  >
    <Icon as={icon} w={8} h={8} color="brand.500" mb={4} />
    <Text fontWeight="bold" fontSize="lg" mb={2}>
      {title}
    </Text>
    <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="sm">
      {description}
    </Text>
  </Box>
);

const Calculator = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const gradientText = useColorModeValue(
    'linear(to-r, brand.500, brand.600)',
    'linear(to-r, brand.400, brand.500)'
  );

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Hero Section */}
      <Box 
        bg={useColorModeValue('white', 'gray.800')} 
        py={12}
        borderBottom="1px"
        borderColor={useColorModeValue('gray.100', 'gray.700')}
      >
        <Container maxW="container.xl">
          <VStack spacing={4} align="center" textAlign="center">
            <Badge 
              colorScheme="brand" 
              fontSize="sm" 
              px={3} 
              py={1} 
              borderRadius="full"
            >
              Financial Tools
            </Badge>
            <Heading 
              size="2xl" 
              bgGradient={gradientText}
              bgClip="text"
            >
              Investment Calculator
            </Heading>
            <Text 
              fontSize="xl" 
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
            >
              Make informed investment decisions with our comprehensive calculator suite.
              Plan your financial future with precision and confidence.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={12}>
          <FeatureCard
            icon={FaCalculator}
            title="SIP & Lumpsum"
            description="Calculate returns for both systematic and one-time investments with ease"
          />
          <FeatureCard
            icon={FaChartLine}
            title="Visual Insights"
            description="Track your investment growth with interactive charts and projections"
          />
          <FeatureCard
            icon={FaPiggyBank}
            title="Smart Planning"
            description="Make data-driven decisions for your long-term financial goals"
          />
        </SimpleGrid>

        <Divider mb={12} />

        {/* Calculator Section */}
        <VStack spacing={8} align="stretch">
          <Box
            bg={useColorModeValue('white', 'gray.800')}
            borderRadius="xl"
            overflow="hidden"
            shadow="xl"
            p={{ base: 4, md: 6 }}
            w="full"
          >
            <Box
              bg={useColorModeValue('brand.50', 'brand.900')}
              p={{ base: 4, md: 6 }}
              borderBottom="1px"
              borderColor={useColorModeValue('brand.100', 'brand.700')}
              mb={6}
            >
              <HStack spacing={4}>
                <Icon as={FaCalculator} w={6} h={6} color="brand.500" />
                <VStack align="start" spacing={1}>
                  <Text fontSize="lg" fontWeight="bold">
                    Investment Calculator
                  </Text>
                  <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.400')}>
                    Calculate your potential returns and plan your investments
                  </Text>
                </VStack>
              </HStack>
            </Box>
            <Box w="full">
              <InvestmentCalculator />
            </Box>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Calculator; 